import clsx from 'clsx'
import React, { useState } from 'react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import s from './styles.module.scss'

import { MAX_TRANSPORT_DURATION } from './MAX_TRANSPORT_DURATION'
import { timeFormat } from './timeFormat'
import { LocationPicker } from '../location-picker'
import { Icon, IconSvg } from '../../atoms'
import { Loader, Modal } from '../../molecules'
import {
  FilterLocationDto,
  Transport_Type,
  TravelCityContext,
  TravelTimeContext,
} from '../../../lib/graphql'

export interface IDurationFromProps {
  className?: string
  onClick?: () => void
  shrink?: boolean
  onChangeLocation?: (location: FilterLocationDto) => void
  isMobile: boolean
  isAuthenticated: boolean
  onClickNotAuthenticated: () => void
}

interface IDurationFrom extends IDurationFromProps {
  city?: TravelCityContext | null
  travelTime?: TravelTimeContext[]
}

export const DurationFrom = ({
  city,
  travelTime = [],
  className,
  onClick,
  shrink = false,
  onChangeLocation,
  isMobile,
  isAuthenticated,
  onClickNotAuthenticated,
}: IDurationFrom) => {
  const intl = useIntl()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const publicTransportDuration =
    travelTime.find((x) => x.type === Transport_Type.TRANSIT)?.duration ||
    undefined
  const carDuration =
    travelTime.find((x) => x.type === Transport_Type.DRIVING)?.duration ||
    undefined

  const {
    control,
    watch,
    formState: { isDirty },
    reset,
  } = useForm<{ location: FilterLocationDto }>({
    defaultValues: {
      location: {
        label: city?.label,
        placeId: city?.placeId,
      },
    },
    mode: 'onChange',
  })

  useEffect(() => {
    if (isDirty && !isLoading && watch('location')) {
      setIsLoading(true)
      reset({ location: watch('location') })
      onChangeLocation && onChangeLocation(watch('location'))
    }
  }, [isDirty, isLoading])

  useEffect(() => {
    reset({
      location: {
        label: city?.label,
        placeId: city?.placeId,
      },
    })
  }, [city])

  useEffect(() => {
    setIsLoading(false)
  }, [travelTime])

  const renderInputField = ({
    inputRef,
    onInputChange,
  }: {
    inputRef: React.LegacyRef<HTMLInputElement> | undefined
    onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  }) => {
    return (
      <div className={s.containerInput}>
        <input
          className={s.input}
          ref={inputRef}
          onChange={onInputChange}
          placeholder={intl.formatMessage({
            id: 'ui.DurationFrom.startingCity',
            defaultMessage: 'Ville de départ',
          })}
        />
      </div>
    )
  }

  const renderSearchBarPicker = ({ value }: { value: string }) => {
    return (
      <div className={s.containerSearchBarPicker}>
        <div className={s.label}>{value}</div>
        <Icon
          name='arrow-down'
          size={10}
          style={{ flexShrink: 0, ...(isMobile && { display: 'none' }) }}
        />
      </div>
    )
  }

  return (
    <div
      className={clsx(className, { [s.onClick]: !!onClick })}
      onClick={() => onClick && onClick()}
    >
      <div
        className={clsx(s.containerStartingFrom, {
          [s.editing]: isEditing,
          [s.shrink]: shrink,
        })}
        onClick={(e) => {
          if (!isAuthenticated) {
            e.stopPropagation()
            onClickNotAuthenticated()
          }
        }}
      >
        <span className={s.from}>
          <FormattedMessage id='ui.DurationFrom.from' defaultMessage='Depuis' />
        </span>
        <LocationPicker
          disabled={!isAuthenticated}
          control={control}
          rules={{ required: false }}
          name='location'
          className={s.locationPicker}
          renderInputField={renderInputField}
          renderSearchBarPicker={renderSearchBarPicker}
          onChangeEditMode={(editing: boolean) => setIsEditing(editing)}
          locationTypes={['locality']}
          shortLabel={true}
          isMobile={isMobile}
        />
      </div>
      {!isEditing && (
        <div className={s.durations}>
          {!!publicTransportDuration &&
            publicTransportDuration <= MAX_TRANSPORT_DURATION && (
              <span className={s.time}>
                <IconSvg.Train />
                {timeFormat(publicTransportDuration)}
              </span>
            )}
          {!!carDuration && (
            <span className={s.time}>
              <IconSvg.Car />
              {timeFormat(carDuration)}
            </span>
          )}
        </div>
      )}

      {isLoading && (
        <Modal isOpen={true}>
          <div className={s.containerLoading}>
            <Loader />
            <div className={s.labelLoading}>
              <FormattedMessage
                id='ui.DurationFrom.travelTimeProcessing'
                defaultMessage='Nous calculons vos temps de transport...'
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default DurationFrom
